import { ErrorState } from '@/components/primitives/ErrorState'
import { PageHeader } from '@/components/primitives/PageHeader'
import { RecordSheet } from '@/components/record-sheet/RecordSheet'
import { fetchTeam } from '@/lib/ai-team'
import { queryClient } from '@/lib/query'
import { type InboxItem, type InboxTab, fetchInbox } from '@/lib/team-memory'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import {
  BellRing,
  CircleDollarSign,
  CircleHelp,
  FileText,
  Inbox as InboxIcon,
  Mail,
  Megaphone,
  Newspaper,
  Scale,
  Search,
  ShieldCheck,
  type LucideIcon
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { AgentAvatar } from './AgentAvatar'
import { timeAgo } from './workflows/shared'

const KIND_ICON: Record<string, LucideIcon> = {
  approval: ShieldCheck,
  question: CircleHelp,
  alert: BellRing,
  draft: FileText,
  ad: Megaphone,
  permit: Scale,
  email: Mail,
  chargeback: CircleDollarSign,
  privacy: Scale,
  copyright: Scale,
  brief: Newspaper
}

const TABS: Array<{ key: InboxTab; label: string; empty: string }> = [
  { key: 'needs', label: 'Needs you', empty: 'Nothing needs you right now. The team is handling the rest.' },
  { key: 'info', label: 'For your info', empty: 'Nothing new to read.' },
  { key: 'done', label: 'Done', empty: 'Nothing finished yet.' }
]

const URGENCY_TONE: Record<string, string> = {
  urgent: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400',
  high: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
}

function due(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000)
  if (days < 0) return 'Overdue'
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  return `Due ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
}

function Row({ item, onOpen }: { item: InboxItem; onOpen: () => void }) {
  const Icon = KIND_ICON[item.kind] ?? InboxIcon
  const dueText = due(item.due)
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex w-full items-start gap-3 rounded-[24px] px-4 py-3.5 text-left transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-900"
    >
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          <p className="min-w-0 flex-1 truncate font-semibold text-neutral-900 dark:text-white">{item.title}</p>
          {URGENCY_TONE[item.urgency] && item.tab === 'needs' && (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold',
                URGENCY_TONE[item.urgency]
              )}
            >
              {item.urgency === 'urgent' ? 'Urgent' : 'Important'}
            </span>
          )}
        </div>
        {item.summary && (
          <p className="mt-0.5 line-clamp-1 text-sm text-neutral-500 dark:text-neutral-400">{item.summary}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="font-semibold text-neutral-700 dark:text-neutral-300">{item.kind_label}</span>
          {item.agent && (
            <span className="inline-flex items-center gap-1">
              <AgentAvatar agentKey={item.agent.key} name={item.agent.name} size="xs" />
              {item.agent.name}
            </span>
          )}
          <span>{item.status_label}</span>
          {item.at && <span>{timeAgo(item.at)}</span>}
          {dueText && item.tab !== 'done' && (
            <span
              className={cn(
                'font-semibold',
                dueText === 'Overdue' ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
              )}
            >
              {dueText}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

/** Everything the team needs from you, in one place: decide, read, or look back. */
export function InboxPage() {
  const [tab, setTab] = useState<InboxTab>('needs')
  const [area, setArea] = useState('')
  const [agent, setAgent] = useState('')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<InboxItem | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), 250)
    return () => window.clearTimeout(t)
  }, [search])

  const inbox = useQuery({
    queryKey: ['ai-inbox', tab, area, agent, q],
    queryFn: () => fetchInbox({ tab, area, agent, q }),
    refetchInterval: 20_000,
    placeholderData: (prev) => prev
  })
  const team = useQuery({ queryKey: ['ai-team'], queryFn: fetchTeam, staleTime: 60_000 })
  const data = inbox.data
  const agents = (team.data?.agents ?? []).map((a) => ({ key: a.key, name: a.name }))
  const current = TABS.find((t) => t.key === tab) ?? TABS[0]

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        title="Needs you"
        description="Everything from the team in one place: what needs your decision, what's worth reading, and what's done."
      />

      <div className="rounded-[36px] border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-black">
        <div className="grid grid-cols-3 gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'rounded-full px-3 py-2 text-sm font-semibold transition-colors',
                tab === t.key
                  ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white'
                  : 'text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white'
              )}
            >
              {t.label}
              {data && data.counts[t.key] > 0 && (
                <span
                  className={cn(
                    'ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] tabular-nums',
                    t.key === 'needs'
                      ? 'bg-red-500 text-white'
                      : 'bg-neutral-200 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200'
                  )}
                >
                  {data.counts[t.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 px-1">
          <label className="relative min-w-[180px] flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search the inbox"
              className="w-full rounded-full border border-neutral-200 bg-white py-2 pl-10 pr-4 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:focus:border-neutral-600"
            />
          </label>
          <select
            value={agent}
            onChange={(e) => setAgent(e.target.value)}
            aria-label="Filter by agent"
            className="rounded-full border border-neutral-200 bg-white px-3.5 py-2 text-sm text-neutral-900 outline-none focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
          >
            <option value="">Every agent</option>
            {agents.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-2 flex flex-wrap gap-1.5 px-1">
          {[{ key: '', label: 'All areas' }, ...(data?.areas ?? [])].map((a) => (
            <button
              key={a.key || 'all'}
              type="button"
              aria-pressed={area === a.key}
              onClick={() => setArea(a.key)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                area === a.key
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                  : 'bg-neutral-100 text-neutral-600 hover:text-neutral-900 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:text-white'
              )}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="mt-3 border-t border-neutral-100 pt-1 dark:border-neutral-800">
          {inbox.error ? (
            <div className="p-4">
              <ErrorState error={inbox.error} onRetry={() => inbox.refetch()} />
            </div>
          ) : !data ? (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
              <InboxIcon className="size-8 text-neutral-300 dark:text-neutral-700" />
              <p className="font-semibold text-neutral-900 dark:text-white">
                {q || area || agent ? 'Nothing matches these filters.' : current.empty}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100 dark:divide-neutral-900">
              {data.items.map((item) => (
                <Row key={item.key} item={item} onOpen={() => setOpen(item)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {open && (
        <RecordSheet
          resourceKey={open.resource}
          id={String(open.id)}
          onClose={() => {
            setOpen(null)
            queryClient.invalidateQueries({ queryKey: ['ai-inbox'] })
          }}
        />
      )}
    </div>
  )
}
